import { type FC } from "react";

import { faFilter } from "@fortawesome/free-solid-svg-icons/faFilter";
import { faSort } from "@fortawesome/free-solid-svg-icons/faSort";
import { faUser } from "@fortawesome/free-solid-svg-icons/faUser";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { EvaluationTypeEnum, ProjectSort } from "@app_schema";

import { Button } from "@styled/button";
import { PillGroup } from "@styled/pill_group";
import { SearchInputField } from "@styled/search_input_field";
import { SelectField } from "@styled/select_field";

type OrgMember = {
  id: string;
  name: string;
};

export const ProjectFilters: FC<{
  query: string | undefined;
  onSearch: (query?: string) => void;
  mine: boolean | undefined;
  onMineChange: (mine?: boolean) => void;
  clinician: boolean;
  sortValue: ProjectSort;
  onSortChange: (sort: ProjectSort) => void;
  assigneeId: string;
  onAssigneeChange: (userId: string) => void;
  members: OrgMember[];
  evaluationType: EvaluationTypeEnum | "";
  onEvaluationTypeChange: (type: EvaluationTypeEnum | "") => void;
}> = ({
  query,
  onSearch,
  mine,
  onMineChange,
  clinician,
  sortValue,
  onSortChange,
  assigneeId,
  onAssigneeChange,
  members,
  evaluationType,
  onEvaluationTypeChange,
}) => {
  // One assignee control folds in the old standalone "Mine" toggle: "mine"
  // sets the `mine` filter, a member id sets `userId`, "" clears both (All).
  const assigneeValue = mine ? "mine" : assigneeId || "";

  const onAssigneeSelect = (value: string) => {
    if (value === "mine") {
      onMineChange(true);
      onAssigneeChange("");
    } else {
      onMineChange(undefined);
      onAssigneeChange(value);
    }
  };

  const sortLabel =
    sortValue === ProjectSort.CreatedNewest ? "Newest first" : "Oldest first";

  const onToggleSort = () =>
    onSortChange(
      sortValue === ProjectSort.CreatedNewest
        ? ProjectSort.CreatedOldest
        : ProjectSort.CreatedNewest,
    );

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="w-[200px] shrink-0 max-md:w-full">
        <SearchInputField query={query} onSearch={onSearch} variant="sm" />
      </div>
      <PillGroup phone="wrap">
        {!clinician && (
          <div className="w-auto shrink-0">
            <SelectField
              name="assignee"
              icon={faUser}
              variant="sm"
              value={assigneeValue}
              onChange={(e) => onAssigneeSelect(e.target.value)}
            >
              <option value="mine">Mine</option>
              <option value="">All clinicians</option>
              {members.map((member) => (
                <option key={member.id} value={member.id}>
                  {member.name}
                </option>
              ))}
            </SelectField>
          </div>
        )}
        <div className="w-auto shrink-0">
          <SelectField
            name="evaluationType"
            icon={faFilter}
            variant="sm"
            value={evaluationType}
            onChange={(e) =>
              onEvaluationTypeChange(e.target.value as EvaluationTypeEnum | "")
            }
          >
            <option value="">All types</option>
            <option value={EvaluationTypeEnum.Comprehensive}>
              Comprehensive
            </option>
            <option value={EvaluationTypeEnum.Supplemental}>
              Supplemental
            </option>
            <option value={EvaluationTypeEnum.Followup}>Follow-up</option>
          </SelectField>
        </div>
        <Button
          type="button"
          color="white"
          size="sm"
          onClick={onToggleSort}
          aria-label={`${sortLabel}. Change the sort order`}
        >
          <FontAwesomeIcon icon={faSort} /> {sortLabel}
        </Button>
      </PillGroup>
    </div>
  );
};
